import * as React from "react";
import { styled } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import MuiToolbar from "@mui/material/Toolbar";
import { tabsClasses } from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StatusChip from "./mainGridComponents/statusChip.component";
import Button from "@mui/material/Button";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  capitalizeFirst,
  formatRelativeTime,
  getLatestSurveyTimestamp,
} from "../../../utils/digitalTwinUtils";


const Toolbar = styled(MuiToolbar)({
  width: "100%",
  padding: "12px",
  display: "flex",
  flexDirection: "column",
  alignItems: "start",
  justifyContent: "center",
  gap: "12px",
  flexShrink: 0,
  [`& ${tabsClasses.flexContainer}`]: {
    gap: "8px",
    p: "8px",
    pb: 0,
  },
});

export default function DigitalTwinNavbar() {
  const [open, setOpen] = React.useState(false);
  const selectedField = useSelector((state) => state.selectedDigitalTwin.field);
  const selectedFarm = useSelector((state) => state.selectedDigitalTwin.farm);
  const selectedPlant = useSelector(
    (state) => state.selectedDigitalTwin.singlePlant,
  );
  const selectedLivestock = useSelector(
    (state) => state.selectedDigitalTwin.singleLivestock,
  );
  const plantsMeasurementsData = useSelector(
    (state) => state.plantsMeasurements?.data?.data,
  );
  const livestockMeasurementsData = useSelector(
    (state) => state.farms?.livestockMeasurements?.data?.data,
  );
  const crop = useSelector((state) => state.selectedField.crop);
  const navigate = useNavigate();
  const latestMeasurementTimestamp = React.useMemo(
    () =>
      getLatestSurveyTimestamp(
        selectedLivestock ? livestockMeasurementsData : plantsMeasurementsData,
      ),
    [livestockMeasurementsData, plantsMeasurementsData, selectedLivestock],
  );

  const title = React.useMemo(() => {
    if (selectedField !== null && selectedPlant !== null) {
      return `${selectedField.field_name} - ${capitalizeFirst(selectedPlant.plant_type)} ${selectedPlant.plant_number}`;
    }

    if (selectedFarm !== null && selectedLivestock !== null) {
      const farmName = selectedFarm?.farm_name || selectedFarm?.name || `Farm ${selectedFarm?.id ?? "-"}`;
      const livestockName =
        selectedLivestock?.livestock_name ||
        selectedLivestock?.name ||
        selectedLivestock?.animal_type ||
        selectedLivestock?.livestock_type ||
        `Capo ${selectedLivestock?.id ?? selectedLivestock?.livestock_id ?? "-"}`;

      return `${farmName} - ${capitalizeFirst(livestockName)}`;
    }

    return "-";
  }, [selectedFarm, selectedField, selectedLivestock, selectedPlant]);

  function getRandomStatus() {
    const statuses = ["ok", "fine", "bad"];
    return statuses[Math.floor(Math.random() * statuses.length)];
  }

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  return (
    <AppBar
      position="static"
      sx={{
        display: "flex",
        flex: 1,
        boxShadow: 0,
        bgcolor: "background.default",
        borderBottom: "1px solid",
        borderColor: "divider",
        top: "var(--template-frame-height, 20px)",
        height: "80px",
        width: "95%",
        marginTop: "80px",
        marginLeft: "2.5%",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Toolbar variant="regular">
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            flexGrow: 1,
            width: "100%",
            gap: 2,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ justifyContent: "center", alignItems: "center", mr: "auto" }}
          >
            <Typography fontSize={"medium"} sx={{ color: "text.primary" }}>
              {title}
            </Typography>
            <StatusChip status={getRandomStatus()} />
            <Typography fontSize={"small"} sx={{ color: "text.secondary" }}>
              Ultima rilevazione: {formatRelativeTime(latestMeasurementTimestamp)}
            </Typography>
          </Stack>
          {/* end of the bar */}
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              navigate(`/digitalTwin`);
            }}
            sx={{
              textTransform: "none",
            }}
          >
            Torna alla Mappa
          </Button>
          <Button
            variant="contained"
            sx={{
              width: "135px",
              backgroundColor: "black",
              textTransform: "none",
            }}
          >
            Genera Report
          </Button>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

export function CustomIcon() {
  return (
    <Box
      sx={{
        width: "1.5rem",
        height: "1.5rem",
        bgcolor: "black",
        borderRadius: "999px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        alignSelf: "center",
        backgroundImage:
          "linear-gradient(135deg, hsl(210, 98%, 60%) 0%, hsl(210, 100%, 35%) 100%)",
        color: "hsla(210, 100%, 95%, 0.9)",
        border: "1px solid",
        borderColor: "hsl(210, 100%, 55%)",
        boxShadow: "inset 0 2px 5px rgba(255, 255, 255, 0.3)",
      }}
    >
      <DashboardRoundedIcon color="inherit" sx={{ fontSize: "1rem" }} />
    </Box>
  );
}
